import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
    Users, Check, Shield, Bell, BarChart3, 
    Heart, AlertTriangle, ArrowRight, Sparkles
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FamilyPricingPage = () => {
    const navigate = useNavigate();
    const [tiers, setTiers] = useState({});
    const [features, setFeatures] = useState({});
    const [alertTypes, setAlertTypes] = useState({});
    const [loading, setLoading] = useState(true);
    const [selectedTier, setSelectedTier] = useState(null);
    const [billingCycle, setBillingCycle] = useState('monthly');

    useEffect(() => {
        fetchTiers();
    }, []);

    const fetchTiers = async () => {
        try {
            const response = await axios.get(`${BACKEND_URL}/api/family/subscription-tiers`);
            setTiers(response.data.tiers || {});
            setFeatures(response.data.features_description || {});
            setAlertTypes(response.data.alert_types || {});
        } catch (error) {
            console.error('Failed to fetch tiers:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectTier = (tierKey) => {
        setSelectedTier(tierKey);
        navigate(`/family/register?tier=${tierKey}&billing=${billingCycle}`);
    };

    const tierIcons = {
        family_basic: Users,
        family_plus: Heart,
        family_complete: Shield
    };

    const tierColors = {
        family_basic: 'from-blue-500 to-cyan-500',
        family_plus: 'from-purple-500 to-pink-500',
        family_complete: 'from-amber-500 to-orange-500'
    };

    return (
        <div className="min-h-screen bg-background">
            <div className="noise-overlay" />
            
            {/* Header */}
            <header className="bg-card/80 backdrop-blur-lg border-b border-border sticky top-0 z-50">
                <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <img src="/icon-branded.png" alt="MoodTrack" className="w-8 h-8 rounded-lg" />
                        <span className="font-heading text-xl font-semibold">MoodTrack Family</span>
                    </Link>
                    <div className="flex items-center gap-4">
                        <Link to="/login">
                            <Button variant="ghost">Sign In</Button>
                        </Link>
                        <Link to="/pricing">
                            <Button variant="outline">Individual Plans</Button>
                        </Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="py-16 px-4">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-6">
                            <Shield className="w-4 h-4" />
                            <span className="text-sm font-medium">Family Safety First</span>
                        </div>
                        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-foreground mb-4">
                            Keep Your Family Connected & Safe
                        </h1>
                        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                            Monitor your children's emotional wellbeing with real-time safety alerts, 
                            comprehensive analytics, and full parental controls.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Billing Toggle */}
            <section className="py-4 px-4">
                <div className="max-w-6xl mx-auto flex justify-center">
                    <div className="inline-flex items-center p-1 rounded-full bg-muted/50">
                        <button
                            onClick={() => setBillingCycle('monthly')}
                            className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                                billingCycle === 'monthly' 
                                    ? 'bg-primary text-primary-foreground shadow-md' 
                                    : 'text-muted-foreground hover:text-foreground'
                            }`}
                            data-testid="billing-monthly-btn"
                        >
                            Monthly
                        </button>
                        <button
                            onClick={() => setBillingCycle('yearly')}
                            className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                                billingCycle === 'yearly' 
                                    ? 'bg-primary text-primary-foreground shadow-md' 
                                    : 'text-muted-foreground hover:text-foreground'
                            }`}
                            data-testid="billing-yearly-btn"
                        >
                            Yearly
                            <span className="ml-2 px-2 py-0.5 bg-green-500/20 text-green-600 rounded-full text-xs">Save 17%</span>
                        </button>
                    </div>
                </div>
            </section>

            {/* Pricing Cards */}
            <section className="py-8 px-4">
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {Object.entries(tiers).map(([key, tier], index) => {
                            const Icon = tierIcons[key] || Users;
                            const gradient = tierColors[key] || 'from-primary to-accent';
                            const isPopular = key === 'family_plus';
                            const price = billingCycle === 'yearly' ? tier.price_yearly : tier.price_monthly;
                            const priceLabel = billingCycle === 'yearly' ? '/year' : '/month';
                            
                            return (
                                <motion.div
                                    key={key}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className={isPopular ? 'md:-mt-4 md:mb-4' : ''}
                                >
                                    <Card className={`glass-card relative overflow-hidden h-full ${isPopular ? 'border-primary shadow-xl' : ''}`}>
                                        {isPopular && (
                                            <div className="absolute top-0 right-0 bg-primary text-primary-foreground px-3 py-1 text-xs font-medium rounded-bl-lg">
                                                Most Popular
                                            </div>
                                        )}
                                        <CardHeader className="pb-4">
                                            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center mb-4`}>
                                                <Icon className="w-6 h-6 text-white" />
                                            </div>
                                            <CardTitle className="font-heading text-2xl">{tier.name}</CardTitle>
                                            <p className="text-sm text-muted-foreground">{tier.description}</p>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="mb-6">
                                                <span className="font-heading text-4xl font-bold">${price}</span>
                                                <span className="text-muted-foreground">{priceLabel}</span>
                                                {billingCycle === 'yearly' && (
                                                    <p className="text-sm text-green-600 mt-1">
                                                        Save {tier.yearly_savings} vs monthly
                                                    </p>
                                                )}
                                            </div>
                                            
                                            <div className="space-y-3 mb-6">
                                                <div className="flex items-center gap-2">
                                                    <Check className="w-4 h-4 text-primary" />
                                                    <span>{tier.max_parents} parent account{tier.max_parents > 1 ? 's' : ''}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Check className="w-4 h-4 text-primary" />
                                                    <span>
                                                        {tier.max_children === -1 ? 'Unlimited' : `Up to ${tier.max_children}`} children
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Check className="w-4 h-4 text-primary" />
                                                    <span>Full safety alerts</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Check className="w-4 h-4 text-primary" />
                                                    <span>Complete analytics dashboard</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Check className="w-4 h-4 text-primary" />
                                                    <span>Custom keyword monitoring</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Check className="w-4 h-4 text-primary" />
                                                    <span>Partner approval controls</span>
                                                </div>
                                            </div>
                                            
                                            <Button 
                                                className={`w-full rounded-full ${isPopular ? '' : 'variant-outline'}`}
                                                variant={isPopular ? 'default' : 'outline'}
                                                onClick={() => handleSelectTier(key)}
                                                data-testid={`select-${key}-btn`}
                                            >
                                                Get Started
                                                <ArrowRight className="w-4 h-4 ml-2" />
                                            </Button>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-16 px-4 bg-muted/30">
                <div className="max-w-6xl mx-auto">
                    <h2 className="font-heading text-3xl font-bold text-center mb-12">
                        All Plans Include
                    </h2>
                    
                    {/* Safety Alerts */}
                    <div className="mb-12">
                        <h3 className="font-heading text-xl font-semibold mb-6 flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-amber-500" />
                            Real-Time Safety Alerts
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <Card className="glass-card">
                                <CardContent className="p-6">
                                    <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center mb-4">
                                        <Bell className="w-5 h-5 text-amber-500" />
                                    </div>
                                    <h4 className="font-medium mb-2">Low Mood Streak</h4>
                                    <p className="text-sm text-muted-foreground">
                                        {alertTypes.low_mood_streak || 'Triggered when child logs 5+ consecutive days with mood below 4/10'}
                                    </p>
                                </CardContent>
                            </Card>
                            <Card className="glass-card">
                                <CardContent className="p-6">
                                    <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center mb-4">
                                        <AlertTriangle className="w-5 h-5 text-red-500" />
                                    </div>
                                    <h4 className="font-medium mb-2">Concerning Keywords</h4>
                                    <p className="text-sm text-muted-foreground">
                                        {alertTypes.concerning_keywords || 'AI scans entries for self-harm, depression, or crisis-related language'}
                                    </p>
                                </CardContent>
                            </Card>
                            <Card className="glass-card">
                                <CardContent className="p-6">
                                    <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center mb-4">
                                        <Heart className="w-5 h-5 text-purple-500" />
                                    </div>
                                    <h4 className="font-medium mb-2">Crisis Resource Access</h4>
                                    <p className="text-sm text-muted-foreground">
                                        {alertTypes.crisis_resource_access || 'Notifies you when your child views the crisis support/resources page'}
                                    </p>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                    
                    {/* Dashboard Features */}
                    <div>
                        <h3 className="font-heading text-xl font-semibold mb-6 flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-primary" />
                            Full Analytics Dashboard
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {Object.entries(features).slice(0, 8).map(([key, description]) => (
                                <div key={key} className="flex items-start gap-2 p-4 rounded-xl bg-card">
                                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="text-sm font-medium capitalize">
                                            {key.replace(/_/g, ' ')}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 px-4">
                <div className="max-w-2xl mx-auto text-center">
                    <h2 className="font-heading text-3xl font-bold mb-4">
                        Ready to protect your family?
                    </h2>
                    <p className="text-muted-foreground mb-8">
                        Start your family plan today and give your children a safe space to journal while staying informed about their emotional wellbeing.
                    </p>
                    <Button 
                        size="lg" 
                        className="rounded-full px-8"
                        onClick={() => handleSelectTier('family_plus')}
                    >
                        <Sparkles className="w-5 h-5 mr-2" />
                        Start Family Plan
                    </Button>
                </div>
            </section>
        </div>
    );
};

export default FamilyPricingPage;
